import React from 'react';
import dayjs from 'utils/dayjs';

import { toCurrency, toPercent } from '../../../helpers/numberHelper';
import './competitorPricing.style.css';
import './competitorPricingWide.style.css';

const CompetitorPricingWidget = ({ dates, records, demand, currentDate, wide }) => {
    const isCurrentDate = (date) => dayjs(date).isSame(currentDate, 'day');

    const getMessage = (otaMessage) => {
        switch (otaMessage) {
            case 'rates.soldout':
                return 'Sold Out';
            case 'rates.restrictionlos2':
                return 'LOS2';
            case 'rates.restrictionlos3':
                return 'LOS3';
            case 'rates.no_person.2':
                return 'Double Occ.';
            default:
                return 'N/A';
        }
    };

    const renderTableHeader = () => {
        const prettyFormat = 'ddd DD MMM';
        return (
            <thead>
                <tr>
                    <th></th>
                    {dates.map((date) => {
                        const className = isCurrentDate(date) ? 'CompetitorPricingHeaderCurrentDate' : '';
                        return (
                            <th key={date} className={className}>
                                {dayjs(date).format(prettyFormat)}
                            </th>
                        );
                    })}
                    <th>Change</th>
                </tr>
            </thead>
        );
    };

    const renderChange = (change, changeDisplayValue) => {
        let arrowClass = '';
        if (change > 0) arrowClass = 'icon-green-up-arrow';
        if (change < 0) arrowClass = 'icon-red-down-arrow';
        return (
            <div className="OTAChange">
                <span className={arrowClass} />
                {changeDisplayValue}
            </div>
        );
    };

    const renderDemandChange = () => {
        const demandChange = demand[currentDate.format('YYYY-MM-DD')];
        if (!demandChange || !demandChange.today || !demandChange.yesterday) {
            return <div className="OTAChange">N/A</div>;
        }

        const change = Math.round(demandChange.today * 100) - Math.round(demandChange.yesterday * 100);
        return renderChange(change, toPercent(Math.abs(change), false));
    };

    const renderDemandRow = () => (
        <tr id="demandRow">
            <td>Market demand</td>
            {dates.map((date) => {
                const className = isCurrentDate(date) ? 'CompetitorPricingHeaderCurrentDate' : '';
                const rawDemand = demand[date] ? demand[date].today : null;
                const prettyDemand = rawDemand ? toPercent(rawDemand) : 'N/A';
                return (
                    <td key={`demand-${date}`} className={className}>
                        {prettyDemand}
                    </td>
                );
            })}
            <td>{renderDemandChange()}</td>
        </tr>
    );

    const renderRateChange = (hotelPricings) => {
        const currentPricings = hotelPricings && hotelPricings.find((p) => isCurrentDate(p.date));
        if (
            !currentPricings ||
            !currentPricings.todayPricing.hasPricing ||
            !currentPricings.yesterdayPricing.hasPricing
        )
            return <div className="OTAChange">N/A</div>;

        const change = (currentPricings.todayPricing.price - currentPricings.yesterdayPricing.price) / 100;
        return renderChange(change, toCurrency(Math.abs(change), currentPricings.todayPricing.currency));
    };

    const renderRowColumns = (rows) =>
        dates.map((date) => {
            const className = isCurrentDate(date) ? 'CompetitorPricingHeaderCurrentDate' : '';
            const match = rows && rows.find((row) => dayjs(row.date).isSame(dayjs(date), 'day'));
            let displayValue = getMessage(match && match.todayPricing.message);
            if (match && match.todayPricing.hasPricing) {
                displayValue = toCurrency(match.todayPricing.price / 100, match.todayPricing.currency);
            }
            return (
                <td key={date} className={className}>
                    {displayValue}
                </td>
            );
        });

    const renderHotelsRows = () =>
        records.map((row) => (
            <tr key={row.hotelId}>
                <td className={wide ? 'CompetitorPricingWideHeader' : 'CompetitorPricingHeader'} title={row.hotelName}>
                    {row.hotelName}
                </td>
                {renderRowColumns(row.pricings)}
                <td>{renderRateChange(row.pricings)}</td>
            </tr>
        ));

    if (!records || records.length === 0) {
        return (
            <div className={wide ? 'CompetitorPricingWideNoData' : 'CompetitorPricingNoData'}>
                No Information Available.
            </div>
        );
    }

    return (
        <table className={wide ? 'CompetitorPricingWide' : 'CompetitorPricing'}>
            {renderTableHeader()}
            <tbody>
                {renderDemandRow()}
                {renderHotelsRows()}
            </tbody>
        </table>
    );
};

export default CompetitorPricingWidget;
